import React, { useState, useEffect, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { User, Search, X } from 'lucide-react';

/**
 * EmployeeSearchByCedula
 * Input que busca empleados por cédula. Si la cédula coincide exacta con uno,
 * lo autoselecciona. Si coincide parcialmente, muestra una lista desplegable
 * con las coincidencias para que el usuario elija.
 *
 * Props:
 *  - employees: array de empleados (debe traer _id, cedula, nombre, apellido)
 *  - value: empleado seleccionado actualmente
 *  - onSelect(empleado): callback cuando se selecciona uno
 *  - placeholder: texto del input
 *  - autoFocus: autofocus
 */
const EmployeeSearchByCedula = ({
  employees = [],
  value,
  onSelect,
  placeholder = 'Digita tu número de identificación...',
  autoFocus = true,
}) => {
  const [cedula, setCedula] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  // Si llega un value externo (ej: por edición), sincroniza el input
  useEffect(() => {
    if (value?.cedula) {
      setCedula(value.cedula);
    }
  }, [value]);

  // Coincidencias filtradas en vivo
  const matches = useMemo(() => {
    const q = cedula.trim().toLowerCase();
    if (!q) return [];
    return employees
      .filter((e) => {
        const ced = String(e.cedula || '').toLowerCase();
        const nom = String(e.nombre || '').toLowerCase();
        const ape = String(e.apellido || '').toLowerCase();
        return ced.includes(q) || nom.includes(q) || ape.includes(q);
      })
      .slice(0, 8);
  }, [cedula, employees]);

  // Si la cédula digitada coincide exacta con un solo empleado, autoselecciona
  useEffect(() => {
    const q = cedula.trim();
    if (!q) return;
    const exact = employees.find((e) => String(e.cedula || '') === q);
    if (exact && (!value || value._id !== exact._id)) {
      onSelect?.(exact);
    }
  }, [cedula, employees, onSelect, value]);

  const handleSelect = (empleado) => {
    setCedula(empleado.cedula || '');
    setShowDropdown(false);
    onSelect?.(empleado);
  };

  const handleClear = () => {
    setCedula('');
    setShowDropdown(false);
    onSelect?.(null);
  };

  const handleKeyDown = (e) => {
    if (!showDropdown || matches.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleSelect(matches[highlightedIndex]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  return (
    <div className="space-y-2 w-full">
      <Label htmlFor="cedula-input" className="text-sm font-medium">
        Número de identificación
      </Label>

      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            id="cedula-input"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            autoFocus={autoFocus}
            value={cedula}
            onChange={(e) => {
              setCedula(e.target.value);
              setShowDropdown(true);
              setHighlightedIndex(0);
            }}
            onFocus={() => setShowDropdown(true)}
            onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="pl-10 pr-10 h-12 text-base"
          />
          {cedula && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              title="Limpiar"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Dropdown de coincidencias */}
        {showDropdown && matches.length > 0 && (
          <ul
            className="absolute z-20 mt-1 w-full bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto"
            role="listbox"
          >
            {matches.map((emp, i) => (
              <li
                key={emp._id}
                role="option"
                aria-selected={i === highlightedIndex}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(emp);
                }}
                onMouseEnter={() => setHighlightedIndex(i)}
                className={`
                  px-3 py-2 cursor-pointer flex items-center gap-2 text-sm
                  ${i === highlightedIndex ? 'bg-blue-50' : 'hover:bg-gray-50'}
                `}
              >
                <User className="h-4 w-4 text-gray-400" />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">
                    {emp.nombre} {emp.apellido}
                  </div>
                  <div className="text-xs text-gray-500">CC {emp.cedula}</div>
                </div>
                {emp.rol && (
                  <span className="text-xs text-gray-500">{emp.rol}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Empleado seleccionado */}
      {value && (
        <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">
            {(value.nombre?.[0] || '').toUpperCase()}
            {(value.apellido?.[0] || '').toUpperCase()}
          </div>
          <div className="flex-1">
            <div className="font-semibold text-gray-900">
              {value.nombre} {value.apellido}
            </div>
            <div className="text-xs text-gray-600">
              CC {value.cedula} {value.rol ? `· ${value.rol}` : ''}
            </div>
          </div>
          <button
            type="button"
            onClick={handleClear}
            className="text-gray-400 hover:text-gray-600"
            title="Cambiar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default EmployeeSearchByCedula;
